<template>
    <ManagePageContainer>
        <ManageSidebar />
        <MainContentWrapper>
            <!-- * 헤더 섹션 -->
            <HeaderSection>
                <HeaderTitle>
                    <h1>이관 관리</h1>
                    <span>기존 데이터를 엑셀 파일로 업로드하여 시스템에 이관합니다</span>
                </HeaderTitle>
                <HeaderActions>
                    <RefreshButton @click="refreshProgress">
                        <Refresh :size="24" :style="{ paddingTop: '3px' }" />새로고침
                    </RefreshButton>
                </HeaderActions>
            </HeaderSection>

            <!-- * 템플릿 다운로드 섹션 -->
            <TemplateSection>
                <TemplateHeader>
                    <TemplateTitle>템플릿 다운로드</TemplateTitle>
                    <TemplateSubtitle>데이터 이관을 위한 엑셀 템플릿을 다운로드하세요</TemplateSubtitle>
                </TemplateHeader>
                <TemplateContent>
                    <TemplateGrid>
                        <TemplateItem v-for="template in templates" :key="template.type">
                            <TemplateInfo>
                                <TemplateIcon>
                                    <component :is="template.icon" :size="20" />
                                </TemplateIcon>
                                <TemplateName>{{ template.name }}</TemplateName>
                            </TemplateInfo>
                            <DownloadButton @click="downloadTemplate(template.type)">
                                다운로드
                            </DownloadButton>
                        </TemplateItem>
                    </TemplateGrid>
                </TemplateContent>
            </TemplateSection>

            <!-- * 이관 진행 상황 -->
            <ProgressSection>
                <ProgressHeader>
                    <ProgressTitle>최근 이관 현황</ProgressTitle>
                    <ProgressSubtitle v-if="lastMigrationDate">
                        마지막 이관: {{ formatDate(lastMigrationDate) }}
                    </ProgressSubtitle>
                </ProgressHeader>
                <ProgressContent>
                    <ProgressGrid>
                        <ProgressCard v-for="stat in migrationStats" :key="stat.type" :type="stat.type">
                            <ProgressNumber :type="stat.type">{{ stat.count }}</ProgressNumber>
                            <ProgressLabel>{{ stat.label }}</ProgressLabel>
                        </ProgressCard>
                    </ProgressGrid>
                    <ProgressSummary v-if="totalMigrations > 0">
                        <SummaryText>총 이관 횟수: <strong>{{ totalMigrations }}회</strong></SummaryText>
                    </ProgressSummary>
                </ProgressContent>
            </ProgressSection>

            <!-- * 데이터 이관 섹션 -->
            <MigrationGrid>
                <!-- 주문서 이관 -->
                <MigrationCard>
                    <CardHeader>
                        <CardIcon type="order">
                            <ClipboardText :size="24" />
                        </CardIcon>
                        <CardTitle>
                            <h3>주문서 이관</h3>
                            <span>기존 주문서 데이터를 이관합니다</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <AlertMessage v-if="uploadResults.order" :type="uploadResults.order.type">
                            <AlertIcon>
                                <component :is="getAlertIcon(uploadResults.order.type)" :size="20" />
                            </AlertIcon>
                            {{ uploadResults.order.message }}
                        </AlertMessage>

                        <UploadArea @click="triggerFileUpload('order')" @drop="handleFileDrop($event, 'order')"
                            @dragover.prevent @dragenter.prevent :class="{ 'dragover': dragStates.order }">
                            <UploadText>
                                <strong>클릭하거나 파일을 드래그하여 업로드</strong><br><br>
                                지원 형식: .xlsx, .xls
                            </UploadText>
                        </UploadArea>

                        <FileInput ref="orderFileInput" type="file" accept=".xlsx,.xls"
                            @change="handleFileSelect($event, 'order')" />

                        <CardActions>
                            <PrimaryButton @click="processUpload('order')"
                                :disabled="!uploadFiles.order || isProcessing.order">
                                <Upload :size="20" :style="{ paddingTop: '3px' }" />
                                {{ isProcessing.order ? '처리 중...' : '주문서 이관 실행' }}
                            </PrimaryButton>
                        </CardActions>
                    </CardContent>
                </MigrationCard>

                <!-- 재고 이관 -->
                <MigrationCard>
                    <CardHeader>
                        <CardIcon type="stock">
                            <Package :size="24" />
                        </CardIcon>
                        <CardTitle>
                            <h3>재고 이관</h3>
                            <span>기존 재고 데이터를 이관합니다</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <AlertMessage v-if="uploadResults.stock" :type="uploadResults.stock.type">
                            <AlertIcon>
                                <component :is="getAlertIcon(uploadResults.stock.type)" :size="20" />
                            </AlertIcon>
                            {{ uploadResults.stock.message }}
                        </AlertMessage>

                        <UploadArea @click="triggerFileUpload('stock')" @drop="handleFileDrop($event, 'stock')"
                            @dragover.prevent @dragenter.prevent :class="{ 'dragover': dragStates.stock }">
                            <UploadText>
                                <strong>클릭하거나 파일을 드래그하여 업로드</strong><br><br>
                                지원 형식: .xlsx, .xls
                            </UploadText>
                        </UploadArea>

                        <FileInput ref="stockFileInput" type="file" accept=".xlsx,.xls"
                            @change="handleFileSelect($event, 'stock')" />

                        <CardActions>
                            <SecondaryButton @click="processUpload('stock')"
                                :disabled="!uploadFiles.stock || isProcessing.stock">
                                <Upload :size="20" :style="{ paddingTop: '3px' }" />
                                {{ isProcessing.stock ? '처리 중...' : '재고 이관 실행' }}
                            </SecondaryButton>
                        </CardActions>
                    </CardContent>
                </MigrationCard>

                <!-- 고객 이관 -->
                <MigrationCard>
                    <CardHeader>
                        <CardIcon type="recipients">
                            <AccountGroup :size="24" />
                        </CardIcon>
                        <CardTitle>
                            <h3>고객 이관</h3>
                            <span>기존 고객 데이터를 이관합니다</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <AlertMessage v-if="uploadResults.recipients" :type="uploadResults.recipients.type">
                            <AlertIcon>
                                <component :is="getAlertIcon(uploadResults.recipients.type)" :size="20" />
                            </AlertIcon>
                            {{ uploadResults.recipients.message }}
                        </AlertMessage>

                        <UploadArea @click="triggerFileUpload('recipients')"
                            @drop="handleFileDrop($event, 'recipients')" @dragover.prevent @dragenter.prevent
                            :class="{ 'dragover': dragStates.recipients }">
                            <UploadText>
                                <strong>클릭하거나 파일을 드래그하여 업로드</strong><br><br>
                                지원 형식: .xlsx, .xls
                            </UploadText>
                        </UploadArea>

                        <FileInput ref="recipientsFileInput" type="file" accept=".xlsx,.xls"
                            @change="handleFileSelect($event, 'recipients')" />

                        <CardActions>
                            <SuccessButton @click="processUpload('recipients')"
                                :disabled="!uploadFiles.recipients || isProcessing.recipients">
                                <Upload :size="20" :style="{ paddingTop: '3px' }" />
                                {{ isProcessing.recipients ? '처리 중...' : '고객 이관 실행' }}
                            </SuccessButton>
                        </CardActions>
                    </CardContent>
                </MigrationCard>

                <!-- 거래처 이관 -->
                <MigrationCard>
                    <CardHeader>
                        <CardIcon type="company">
                            <Domain :size="24" />
                        </CardIcon>
                        <CardTitle>
                            <h3>거래처 이관</h3>
                            <span>기존 거래처 데이터를 이관합니다</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <AlertMessage v-if="uploadResults.company" :type="uploadResults.company.type">
                            <AlertIcon>
                                <component :is="getAlertIcon(uploadResults.company.type)" :size="20" />
                            </AlertIcon>
                            {{ uploadResults.company.message }}
                        </AlertMessage>

                        <UploadArea @click="triggerFileUpload('company')" @drop="handleFileDrop($event, 'company')"
                            @dragover.prevent @dragenter.prevent :class="{ 'dragover': dragStates.company }">
                            <UploadText>
                                <strong>클릭하거나 파일을 드래그하여 업로드</strong><br><br>
                                지원 형식: .xlsx, .xls
                            </UploadText>
                        </UploadArea>

                        <FileInput ref="companyFileInput" type="file" accept=".xlsx,.xls"
                            @change="handleFileSelect($event, 'company')" />

                        <CardActions>
                            <ActionButton @click="processUpload('company')"
                                :disabled="!uploadFiles.company || isProcessing.company"
                                style="background: #8b5cf6; color: white;">
                                <Upload :size="20" :style="{ paddingTop: '3px' }" />
                                {{ isProcessing.company ? '처리 중...' : '거래처 이관 실행' }}
                            </ActionButton>
                        </CardActions>
                    </CardContent>
                </MigrationCard>

                <!-- 산소발생기 이관 -->
                <MigrationCard>
                    <CardHeader>
                        <CardIcon type="oxygen">
                            <GasCylinder :size="24" />
                        </CardIcon>
                        <CardTitle>
                            <h3>산소발생기 이관</h3>
                            <span>공단에 등록된 산소발생기를 이관합니다</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <AlertMessage v-if="uploadResults.oxygen" :type="uploadResults.oxygen.type">
                            <AlertIcon>
                                <component :is="getAlertIcon(uploadResults.oxygen.type)" :size="20" />
                            </AlertIcon>
                            {{ uploadResults.oxygen.message }}
                        </AlertMessage>

                        <UploadArea @click="triggerFileUpload('oxygen')" @drop="handleFileDrop($event, 'oxygen')"
                            @dragover.prevent @dragenter.prevent :class="{ 'dragover': dragStates.oxygen }">
                            <UploadText>
                                <strong>클릭하거나 파일을 드래그하여 업로드</strong><br><br>
                                지원 형식: .xlsx, .xls
                            </UploadText>
                        </UploadArea>

                        <FileInput ref="oxygenFileInput" type="file" accept=".xlsx,.xls"
                            @change="handleFileSelect($event, 'oxygen')" />

                        <CardActions>
                            <ActionButton @click="processUpload('oxygen')"
                                :disabled="!uploadFiles.oxygen || isProcessing.oxygen"
                                style="background: #2563eb; color: white;">
                                <Upload :size="20" :style="{ paddingTop: '3px' }" />
                                {{ isProcessing.oxygen ? '처리 중...' : '산소발생기 이관 실행' }}
                            </ActionButton>
                        </CardActions>
                    </CardContent>
                </MigrationCard>

                <!-- 당뇨소모성재료 이관 -->
                <MigrationCard>
                    <CardHeader>
                        <CardIcon type="diabetes">
                            <Diabetes :size="24" />
                        </CardIcon>
                        <CardTitle>
                            <h3>당뇨소모성재료 이관</h3>
                            <span>공단에 등록된 당뇨소모성재료를 이관합니다</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <AlertMessage v-if="uploadResults.diabetes" :type="uploadResults.diabetes.type">
                            <AlertIcon>
                                <component :is="getAlertIcon(uploadResults.diabetes.type)" :size="20" />
                            </AlertIcon>
                            {{ uploadResults.diabetes.message }}
                        </AlertMessage>

                        <UploadArea @click="triggerFileUpload('diabetes')" @drop="handleFileDrop($event, 'diabetes')"
                            @dragover.prevent @dragenter.prevent :class="{ 'dragover': dragStates.diabetes }">
                            <UploadText>
                                <strong>클릭하거나 파일을 드래그하여 업로드</strong><br><br>
                                지원 형식: .xlsx, .xls
                            </UploadText>
                        </UploadArea>

                        <FileInput ref="diabetesFileInput" type="file" accept=".xlsx,.xls"
                            @change="handleFileSelect($event, 'diabetes')" />

                        <CardActions>
                            <ActionButton @click="processUpload('diabetes')"
                                :disabled="!uploadFiles.diabetes || isProcessing.diabetes"
                                style="background: #b91c1c; color: white;">
                                <Upload :size="20" :style="{ paddingTop: '3px' }" />
                                {{ isProcessing.diabetes ? '처리 중...' : '당뇨소모성재료 이관 실행' }}
                            </ActionButton>
                        </CardActions>
                    </CardContent>
                </MigrationCard>
            </MigrationGrid>

        </MainContentWrapper>
    </ManagePageContainer>
</template>

<script>
import * as ManageMigrationCss from '@/assets/styles/manage/ManageMigrationCss';
import ManageSidebar from './ManageSidebar.vue';

import ClipboardText from 'vue-material-design-icons/ClipboardText.vue';
import Package from 'vue-material-design-icons/Package.vue';
import AccountGroup from 'vue-material-design-icons/AccountGroup.vue';
import Domain from 'vue-material-design-icons/Domain.vue';
import Upload from 'vue-material-design-icons/Upload.vue';
import Refresh from 'vue-material-design-icons/Refresh.vue';
import CheckCircle from 'vue-material-design-icons/CheckCircle.vue';
import AlertCircle from 'vue-material-design-icons/AlertCircle.vue';
import Information from 'vue-material-design-icons/Information.vue';
import Alert from 'vue-material-design-icons/Alert.vue';
import FileExcel from 'vue-material-design-icons/FileExcel.vue';
import GasCylinder from 'vue-material-design-icons/GasCylinder.vue';
import Diabetes from 'vue-material-design-icons/Diabetes.vue';

export default {
    name: 'ManageMigration',

    components: {
        ...ManageMigrationCss,
        ManageSidebar,
        ClipboardText,
        Package,
        AccountGroup,
        Domain,
        Upload,
        Refresh,
        CheckCircle,
        AlertCircle,
        Information,
        Alert,
        FileExcel,
        GasCylinder,
        Diabetes
    },

    data() {
        return {
            templates: [
                {
                    type: 'order',
                    name: '주문서 템플릿',
                    icon: 'ClipboardText'
                },
                {
                    type: 'stock',
                    name: '재고 템플릿',
                    icon: 'Package'
                },
                {
                    type: 'recipients',
                    name: '고객 템플릿',
                    icon: 'AccountGroup'
                },
                {
                    type: 'company',
                    name: '거래처 템플릿',
                    icon: 'Domain'
                }
            ],
            migrationStats: [
                {
                    type: 'order',
                    label: '최근 이관된 주문서',
                    count: 0
                },
                {
                    type: 'stock',
                    label: '최근 이관된 재고',
                    count: 0
                },
                {
                    type: 'recipients',
                    label: '최근 이관된 고객',
                    count: 0
                },
                {
                    type: 'company',
                    label: '최근 이관된 거래처',
                    count: 0
                },
                {
                    type: 'oxygen',
                    label: '최근 이관된 산소발생기',
                    count: 0
                }
            ],
            uploadFiles: {
                order: null,
                stock: null,
                recipients: null,
                company: null
            },
            uploadResults: {
                order: null,
                stock: null,
                recipients: null,
                company: null
            },
            isProcessing: {
                order: false,
                stock: false,
                recipients: false,
                company: false
            },
            dragStates: {
                order: false,
                stock: false,
                recipients: false,
                company: false
            },
            // 이관 통계 관련 데이터
            lastMigrationDate: null,
            totalMigrations: 0
        };
    },

    mounted() {
        this.loadMigrationStats();
        this.setupDragAndDrop();
    },

    methods: {
        // 산소발생기 크롤링 → DB 저장
        async runOxygenCrawlingStep() {
            const company = this.$globalState.company;
            const companykey = this.$globalState.company_key;

            // 회사명 입력
            const { value: companyName, isConfirmed } = await this.$swal.fire({
                title: '산소발생기 목록 가져오기',
                input: 'text',
                inputLabel: '가져올 회사명',
                inputValue: company || '',
                inputPlaceholder: '회사명을 입력하세요',
                showCancelButton: true,
                confirmButtonText: '가져오기',
                cancelButtonText: '취소',
                inputValidator: (value) => {
                    if (!value || !value.trim()) {
                        return '회사명을 입력해주세요.';
                    }
                }
            });
            if (!isConfirmed || !companyName) return { success: false, cancelled: true };

            // 로딩
            const crawlingLoading = this.$swal.fire({
                title: '가져오는 중...',
                text: `'${companyName}' 산소발생기 목록을 가져오는 중입니다.`,
                allowOutsideClick: false,
                didOpen: () => this.$swal.showLoading()
            });

            // 크롤링 실행
            const crawlingResult = await window.electronAPI.startCrawling({
                type: 'navigate-oxygen-list',
                companyName: companyName
            });

            crawlingLoading.close();

            if (!crawlingResult?.success) {
                await this.$swal.fire({
                    title: '오류',
                    text: crawlingResult?.error || '산소발생기 목록 페이지 이동 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
                return { success: false };
            }

            const products = crawlingResult.products || [];
            if (products.length === 0) {
                const msg = crawlingResult.warning
                    ? `${crawlingResult.message}\n경고: ${crawlingResult.warning}`
                    : crawlingResult.message || '가져온 제품 데이터가 없습니다.';
                await this.$swal.fire({
                    title: '알림',
                    html: `<div style="text-align:left;">
                         <p>${msg}</p>
                         <p style="color:#999;font-size:12px;margin-top:10px;">
                           크롤링 결과: ${JSON.stringify(crawlingResult, null, 2).substring(0, 200)}...
                         </p>
                       </div>`,
                    icon: 'warning',
                    confirmButtonText: '확인'
                });
                return { success: false };
            }

            // 저장
            const savingLoading = this.$swal.fire({
                title: '저장 중...',
                text: `${products.length}개의 산소발생기 정보를 저장하는 중입니다.`,
                allowOutsideClick: false,
                didOpen: () => this.$swal.showLoading()
            });

            try {
                const saveResponse = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/bulkRegisterOxygen`,
                    { products, companykey }
                );

                savingLoading.close();

                if (saveResponse.data?.success) {
                    const result = saveResponse.data.data;
                    await this.$swal.fire({
                        title: '저장 완료',
                        html: `<div style="text-align:left;">
                             <p><strong>총 ${result.total}개</strong>의 제품 정보를 처리했습니다.</p>
                             <p style="color:#28a745;">✓ 새로 저장: ${result.inserted}개</p>
                             <p style="color:#ffc107;">⊘ 중복/무효: ${result.skipped}개</p>
                           </div>`,
                        icon: 'success',
                        confirmButtonText: '확인'
                    });

                    // 통계 업데이트(선택)
                    try {
                        await this.updateMigrationStats('oxygen', result.inserted || 0);
                        await this.loadMigrationStats();
                    } catch { }

                    return {
                        success: true,
                        inserted: result.inserted || 0,
                        total: result.total || 0,
                        products: products
                    };
                } else {
                    throw new Error(saveResponse.data?.message || '데이터 저장에 실패했습니다.');
                }
            } catch (err) {
                savingLoading.close();
                await this.$swal.fire({
                    title: '저장 실패',
                    text: err.message || '산소발생기 목록 저장 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인'
                });
                return { success: false };
            }
        },

        // 템플릿 다운로드
        async downloadTemplate(type) {
            try {
                const loading = this.$swal.fire({
                    title: '템플릿 다운로드 중...',
                    text: '잠시만 기다려주세요.',
                    allowOutsideClick: false,
                });

                let response;
                let apiUrl;
                let fileName;

                // 주문서 템플릿: 일반/산소 선택
                let prefillOxygen = false;
                if (type === 'order') {
                    loading.close();
                    const { value: selected } = await this.$swal.fire({
                        title: '주문서 템플릿 유형 선택',
                        input: 'radio',
                        inputOptions: {
                            normal: '일반 주문서 템플릿',
                            oxygen: '산소발생기 주문서 템플릿'
                        },
                        inputValue: 'normal',
                        showCancelButton: true,
                        confirmButtonText: '확인',
                        cancelButtonText: '취소',
                        customClass: {
                            popup: 'swal-radio-vertical'
                        }
                    });
                    if (!selected) return;

                    if (selected === 'oxygen') {
                        prefillOxygen = true;

                        // 크롤링 → products 확보
                        const crawlRes = await this.runOxygenCrawlingStep();

                        // 서버에 사전기입 템플릿 생성 요청(POST, blob)
                        const directAxios = (await import('axios')).default;
                        const postUrl = `${this.$apiUrls.hanqmedi}/downloadOrderExcelTemplatePrefill`;
                        const payload = {
                            companykey: this.$globalState.company_key,
                            products: crawlRes.products.map(p => ({
                                productNumber: p.productNumber,
                                productName: p.productName
                            }))
                        };

                        const res = await directAxios.post(postUrl, payload, {
                            responseType: 'blob',
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'Content-Type': 'application/json'
                            },
                            timeout: 60000
                        });

                        // 파일 저장
                        const blob = new Blob([res.data], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        });
                        const prefillName = '주문서 이관 템플릿(산소-자동기입).xlsx';

                        if (this.$env.isElectron && window.electronAPI?.saveFile) {
                            const base64Data = await this.blobToBase64(blob);
                            const saveRes = await window.electronAPI.saveFile(prefillName, base64Data);
                            if (!saveRes.success) throw new Error(saveRes.error || '파일 저장 실패');
                        } else {
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = prefillName;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                        }

                        await this.$swal.fire('다운로드 완료', '산소발생기 주문서 템플릿이 저장되었습니다.', 'success');
                        return;
                    }
                }

                // 기본(일반) 템플릿 다운로드 (기존 로직)
                switch (type) {
                    case 'recipients':
                        apiUrl = `${this.$apiUrls.hanqmedi}/downloadBeneficiaryExcelTemplate`;
                        fileName = '고객 이관 템플릿.xlsx';
                        break;
                    case 'company':
                        apiUrl = `${this.$apiUrls.hanqmedi}/downloadCompanyExcelTemplate`;
                        fileName = '거래처 이관 템플릿.xlsx';
                        break;
                    case 'order':
                        apiUrl = `${this.$apiUrls.hanqmedi}/downloadOrderExcelTemplate`;
                        fileName = '주문서 이관 템플릿.xlsx';
                        break;
                    case 'stock':
                        apiUrl = `${this.$apiUrls.hanqmedi}/downloadStockExcelTemplate`;
                        fileName = '재고 이관 템플릿.xlsx';
                        break;
                    default:
                        throw new Error('지원하지 않는 템플릿 타입입니다.');
                }

                // Electron 우선: IPC 다운로드 지원 시
                if (this.$env.isElectron && window.electronAPI?.downloadFile) {
                    const result = await window.electronAPI.downloadFile(apiUrl, fileName);
                    if (result.success) {
                        loading.close();
                        this.$swal.fire('다운로드 완료', '엑셀 템플릿이 다운로드 폴더에 저장되었습니다.', 'success');
                        return;
                    }
                }

                // 웹/폴백: axios GET blob
                const directAxios = (await import('axios')).default;
                response = await directAxios.get(apiUrl, {
                    responseType: 'blob',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    timeout: 30000
                });

                loading.close();

                if (!response.data || response.data.size === 0) throw new Error('다운로드된 파일이 비어있습니다.');

                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                if (this.$env.isElectron && window.electronAPI?.saveFile) {
                    const base64Data = await this.blobToBase64(blob);
                    const result = await window.electronAPI.saveFile(fileName, base64Data);
                    if (result.success) {
                        await this.$swal.fire('다운로드 완료', `파일이 저장되었습니다: ${result.filePath}`, 'success');
                        return;
                    }
                }

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);

                this.$swal.fire('다운로드 완료', '엑셀 템플릿이 성공적으로 다운로드되었습니다.', 'success');
            } catch (error) {
                console.error('템플릿 다운로드 오류:', error);
                this.$swal.fire('다운로드 실패', error.message || '엑셀 템플릿 다운로드 중 오류가 발생했습니다.', 'error');
            }
        },

        // 드래그 앤 드롭 처리
        handleFileDrop(event, type) {
            event.preventDefault();
            this.dragStates[type] = false;

            const files = event.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0];
                if (this.isValidExcelFile(file)) {
                    this.uploadFiles[type] = file;
                    this.uploadResults[type] = {
                        type: 'info',
                        message: `파일 선택됨: ${file.name}`
                    };
                } else {
                    this.uploadResults[type] = {
                        type: 'error',
                        message: '지원하지 않는 파일 형식입니다. Excel 파일(.xlsx, .xls)만 업로드 가능합니다.'
                    };
                }
            }
        },

        // 이관 처리
        async processUpload(type) {
            if (!this.uploadFiles[type]) {
                this.$swal.fire({
                    title: '오류',
                    text: '파일을 선택해주세요.',
                    icon: 'error'
                });
                return;
            }

            // 파일 확장자 체크
            const fileExt = this.uploadFiles[type].name.split('.').pop().toLowerCase();
            if (fileExt !== 'xlsx') {
                this.$swal.fire('오류', 'Excel 파일(.xlsx)만 업로드 가능합니다.', 'error');
                return;
            }

            this.isProcessing[type] = true;

            try {
                // 로딩 표시
                const loading = this.$swal.fire({
                    title: `${this.getTypeLabel(type)} 이관 중...`,
                    text: '잠시만 기다려주세요.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                let response;
                let apiUrl;

                // 타입별 API URL 설정
                switch (type) {
                    case 'recipients':
                        apiUrl = `${this.$apiUrls.hanqmedi}/uploadRecipientsExcel`;
                        break;
                    case 'company':
                        apiUrl = `${this.$apiUrls.hanqmedi}/uploadCompanyExcel`;
                        break;
                    case 'order':
                        apiUrl = `${this.$apiUrls.hanqmedi}/uploadOrderExcel`;
                        break;
                    case 'stock':
                        apiUrl = `${this.$apiUrls.hanqmedi}/uploadStockExcel`;
                        break;
                    case 'oxygen':
                        apiUrl = `${this.$apiUrls.hanqmedi}/uploadOxygenExcel`;
                        break;
                    default:
                        throw new Error('지원하지 않는 이관 타입입니다.');
                }

                // 일렉트론 환경에서 IPC 파일 업로드 사용
                if (this.$env.isElectron) {
                    // 파일을 base64로 변환
                    const base64Data = await this.fileToBase64(this.uploadFiles[type]);

                    // 타입별 필드명 매핑 (백엔드에서 기대하는 필드명)
                    const fieldNameMap = {
                        'recipients': 'recipients',
                        'company': 'company',
                        'order': 'order',
                        'stock': 'stock',
                        'oxygen': 'oxygen'
                    };
                    const fieldName = fieldNameMap[type] || type;

                    // 데이터 구조 수정 - fileData를 직접 전달
                    const uploadData = {
                        [fieldName]: {
                            isFile: true,
                            fileName: this.uploadFiles[type].name,
                            fileData: base64Data,
                            fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        },
                        companykey: this.$globalState.company_key
                    };

                    // 토큰을 config로 전달
                    const config = {
                        token: localStorage.getItem('token')
                    };

                    // IPC를 통한 파일 업로드
                    const uploadResult = await window.electronAPI.uploadFile(type, uploadData, config);

                    if (uploadResult.success) {
                        response = { data: uploadResult.data };
                    } else {
                        throw new Error(uploadResult.error || '파일 업로드 실패');
                    }

                } else {
                    // 웹 환경에서 FormData 사용
                    const formData = new FormData();
                    formData.append(type, this.uploadFiles[type]);
                    formData.append('companykey', this.$globalState.company_key);

                    const directAxios = (await import('axios')).default;

                    response = await directAxios.post(apiUrl, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        timeout: 300000,
                        maxContentLength: 100 * 1024 * 1024
                    });
                }

                loading.close();

                // 상세 결과 정보 구성
                const result = response.data.data || response.data;
                const totalRows = (result.rowsProcessed || 0) + (result.rowsFailed || 0);
                const successCount = result.rowsProcessed || 0;

                let resultHtml = `
                    <div style="text-align: center; margin-bottom: 15px;">
                        <h4 style="color: #28a745; margin-bottom: 10px;">📊 ${this.getTypeLabel(type)} 이관 결과</h4>
                        <p><strong>총 처리 건수:</strong> ${totalRows}건</p>
                        <p><strong>성공:</strong> <span style="color: #28a745;">${successCount}건</span></p>
                        <p><strong>실패:</strong> <span style="color: #dc3545;">${result.rowsFailed || 0}건</span></p>
                    </div>
                `;

                // 실패한 행이 있는 경우 상세 정보 표시
                if (result.rowsFailed > 0 && result.failedRows && result.failedRows.length > 0) {
                    resultHtml += `
                        <div style="text-align: center; margin-top: 15px;">
                            <h5 style="color: #dc3545; margin-bottom: 10px;">❌ 실패한 행 상세정보</h5>
                            <div style="max-height: 200px; overflow-y: auto; background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
                    `;

                    result.failedRows.forEach(({ rowIndex, error, data }) => {
                        resultHtml += `
                            <div style="margin-bottom: 8px; padding: 5px; border-left: 3px solid #dc3545; background: white;">
                                <strong>${rowIndex}번 행:</strong> ${error}<br>
                                <small style="color: #6c757d;">데이터: ${JSON.stringify(data)}</small>
                            </div>
                        `;
                    });

                    resultHtml += `
                            </div>
                        </div>
                    `;
                }

                this.$swal.fire({
                    title: `${this.getTypeLabel(type)} 이관 완료`,
                    html: resultHtml,
                    icon: 'success',
                    width: '600px',
                    confirmButtonText: '확인',
                    showCloseButton: true
                });

                this.uploadResults[type] = {
                    type: 'success',
                    message: `${successCount}개의 데이터가 성공적으로 이관되었습니다.`
                };

                // 이관 성공 시 통계 업데이트
                if (successCount > 0) {
                    await this.updateMigrationStats(type, successCount);
                }

                // 통계 새로고침
                await this.loadMigrationStats();

            } catch (error) {
                console.error('업로드 오류:', error);

                // 더 자세한 에러 정보 로깅
                if (error.response) {
                    console.error('서버 응답 상세:', {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        data: error.response.data,
                        headers: error.response.headers
                    });
                }

                let errorMessage = '데이터 이관 중 오류가 발생했습니다.';

                if (error.response) {
                    switch (error.response.status) {
                        case 400:
                            errorMessage = error.response.data.message || '잘못된 요청입니다.';
                            break;
                        case 401:
                            errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
                            break;
                        case 413:
                            errorMessage = '파일 크기가 너무 큽니다.';
                            break;
                        case 500:
                            errorMessage = error.response.data.message || '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
                            break;
                        default:
                            errorMessage = `서버 오류 (${error.response.status}): ${error.response.data.message || error.response.statusText}`;
                    }
                } else if (error.code === 'ECONNABORTED') {
                    errorMessage = '업로드 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.';
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.uploadResults[type] = {
                    type: 'error',
                    message: errorMessage
                };

                this.$swal.fire({
                    title: '이관 실패',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인'
                });
            } finally {
                this.isProcessing[type] = false;
            }
        },

        // 이관 통계 업데이트
        async updateMigrationStats(migrationType, successCount) {
            try {
                // oxygen과 diabetes는 order로 통합 처리
                const normalizedType = (migrationType === 'oxygen' || migrationType === 'diabetes')
                    ? 'order'
                    : migrationType;

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/migration/stats/update`, {
                    companykey: this.$globalState.company_key,
                    migrationType: normalizedType,
                    successCount: successCount
                }, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data.success) {
                    console.log('이관 통계 업데이트 성공:', {
                        migrationType: normalizedType,
                        originalType: migrationType,
                        successCount
                    });
                } else {
                    console.error('이관 통계 업데이트 실패:', response.data.message);
                }
            } catch (error) {
                console.error('이관 통계 업데이트 오류:', error);
            }
        },

        // 이관 통계 로드
        async loadMigrationStats() {
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/migration/stats`, {
                    params: {
                        companykey: this.$globalState.company_key
                    },
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                console.log(response.data);

                if (response.data.success) {
                    const stats = response.data.data;
                    this.migrationStats = [
                        {
                            type: 'order',
                            label: '최근 이관된 주문서',
                            count: stats.order_success_count || 0
                        },
                        {
                            type: 'stock',
                            label: '최근 이관된 재고',
                            count: stats.stock_success_count || 0
                        },
                        {
                            type: 'recipients',
                            label: '최근 이관된 고객',
                            count: stats.recipients_success_count || 0
                        },
                        {
                            type: 'company',
                            label: '최근 이관된 거래처',
                            count: stats.company_success_count || 0
                        }
                    ];

                    // 추가 통계 정보 저장
                    this.lastMigrationDate = stats.last_migration_date;
                    this.totalMigrations = stats.total_migrations || 0;
                }
            } catch (error) {
                console.error('통계 로드 오류:', error);
                // 기본값으로 설정
                this.migrationStats = [
                    { type: 'order', label: '최근 이관된 주문서', count: 0 },
                    { type: 'stock', label: '최근 이관된 재고', count: 0 },
                    { type: 'recipients', label: '최근 이관된 고객', count: 0 },
                    { type: 'company', label: '최근 이관된 거래처', count: 0 }
                ];
                this.lastMigrationDate = null;
                this.totalMigrations = 0;
            }
        },

        // 파일 업로드 트리거
        triggerFileUpload(type) {
            try {
                const fileInput = this.$refs[`${type}FileInput`];
                if (fileInput && fileInput.$el) {
                    fileInput.$el.click();
                } else if (fileInput && fileInput.click) {
                    fileInput.click();
                } else {
                    const inputElement = document.querySelector(`input[type="file"][data-type="${type}"]`);
                    if (inputElement) {
                        inputElement.click();
                    } else {
                        console.error(`파일 입력 요소를 찾을 수 없습니다: ${type}`);
                        this.$swal.fire({
                            title: '오류',
                            text: '파일 업로드 기능을 사용할 수 없습니다.',
                            icon: 'error'
                        });
                    }
                }
            } catch (error) {
                console.error('파일 업로드 트리거 오류:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '파일 업로드 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        // 파일 선택 처리
        handleFileSelect(event, type) {
            const file = event.target.files[0];
            if (file) {
                this.uploadFiles[type] = file;
                this.uploadResults[type] = {
                    type: 'info',
                    message: `파일 선택됨: ${file.name}`
                };
            }
        },

        // blob을 base64로 변환하는 메서드
        blobToBase64(blob) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },

        // 파일을 base64로 변환하는 메서드
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            });
        },

        // 파일 유효성 검사
        isValidExcelFile(file) {
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];
            return validTypes.includes(file.type) ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls');
        },

        // 드래그 앤 드롭 설정
        setupDragAndDrop() {
            const types = ['order', 'stock', 'recipients', 'company'];

            types.forEach(type => {
                const uploadArea = this.$el.querySelector(`[data-type="${type}"] .upload-area`);
                if (uploadArea) {
                    uploadArea.addEventListener('dragenter', () => {
                        this.dragStates[type] = true;
                    });

                    uploadArea.addEventListener('dragleave', (e) => {
                        if (!uploadArea.contains(e.relatedTarget)) {
                            this.dragStates[type] = false;
                        }
                    });
                }
            });
        },

        // 날짜 포맷팅
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleString('ko-KR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        },

        // 타입별 라벨 가져오기
        getTypeLabel(type) {
            const labels = {
                'order': '주문서',
                'stock': '재고',
                'recipients': '고객',
                'company': '거래처',
                'oxygen': '산소발생기'
            };
            return labels[type] || type;
        },

        // 진행 상황 새로고침
        async refreshProgress() {
            await this.loadMigrationStats();
        },

        // 알림 아이콘 가져오기
        getAlertIcon(type) {
            switch (type) {
                case 'success':
                    return 'CheckCircle';
                case 'error':
                    return 'AlertCircle';
                case 'warning':
                    return 'Alert';
                case 'info':
                default:
                    return 'Information';
            }
        },
    }
};
</script>

<style>
/* scoped 제거 - SweetAlert2는 전역 스타일이 필요합니다 */
.swal-radio-vertical .swal2-radio label {
    display: block !important;
    margin-bottom: 15px !important;
    cursor: pointer !important;
}

.swal-radio-vertical .swal2-radio {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-start !important;
}
</style>